import React, { useEffect, useState, Fragment } from 'react';
import { Check, ChevronDown, Sparkles, Calendar, Mail } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { Reveal } from '../components/ui/Reveal';
import { Button } from '../components/ui/Button';
import { MarketplaceModal } from '../components/MarketplaceModal';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
interface Tier {
  name: string;
  desc: string;
  monthly: string;
  annual: string;
  note: string;
  cta: string;
  features: string[];
  highlight: boolean;
}
const tiers: Tier[] = [{
  name: 'Casting',
  desc: 'Post casting calls and receive custom submissions',
  monthly: '$149',
  annual: '$125',
  note: '40% off Beta pricing',
  cta: 'Try Casting',
  features: ['30-day free trial', 'Post unlimited casting calls', 'Access a network of 400+ talent agencies', 'Receive talent submissions & quotes from talent agencies', 'Live influencer metrics & audience demographic data', 'Direct talent manager contact details', 'Search & filter submissions', '3 users'],
  highlight: false
}, {
  name: 'Marketplace',
  desc: 'Search and discover represented talent',
  monthly: '$239',
  annual: '$199',
  note: '40% off Beta pricing',
  cta: 'Request Marketplace Access',
  features: ['Approval required', '30-day free trial', 'Everything in Casting', 'Search 6,000 represented influencers', 'Browse 400+ talent agency rosters', 'Marketplace insights & pricing benchmarks', 'Consulting sessions', 'Unlimited users', 'Priority customer support'],
  highlight: true
}];
const faqs = [{
  q: 'How does the free trial work?',
  a: 'Both Casting and Marketplace plans come with a 30-day free trial so you can explore the platform and post your first casting call risk-free.'
}, {
  q: 'Why does Marketplace require approval?',
  a: 'The roster database and talent agency directory contain sensitive contact and pricing information. We vet users to ensure a high-quality environment for our talent agencies.'
}, {
  q: 'Can I switch plans later?',
  a: 'Anytime. Plans are monthly or annual, and you can upgrade or downgrade whenever your needs change.'
}, {
  q: 'Who owns the creator data?',
  a: 'Talent agencies own and manage their creators’ profiles. Brands and agencies always see live, agency-maintained data that stays current.'
}];
const stats = ['400+ Talent Agencies', '6,000+ Represented Influencers', '$500K+ GMV Facilitated'];
const SEO = {
  title: 'Pricing for Brands and Marketing Agencies | Live Talent Agency Roster Database & Casting Calls | Zano',
  description: 'Compare Zano plans for brands and marketing agencies. Access live talent agency rosters, discover represented creators, connect with talent managers, and post unlimited casting calls.',
  keywords: 'influencer marketing pricing, talent agency roster database, represented creators, influencer casting calls, talent agency directory, creator discovery platform',
  ogTitle: 'Pricing | Zano Influencer Marketplace',
  ogDescription: "Explore pricing for brands and marketing agencies using Zano's marketplace for represented creators."
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
const FaqItem = ({
  q,
  a
}: {
  q: string;
  a: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-gray-200 py-5" data-id="element-2700" data-mp="2ebzehdnamkf5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left gap-4" data-id="element-2701" data-mp="2t3i0pt6qxb6y">
        <span className="font-semibold text-gray-900" data-id="element-2702" data-mp="2oa5cvl9w58f9">{q}</span>
        <ChevronDown size={20} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-2703" data-mp="3223cn5hppryr" />
      </button>
      {open && <p className="mt-3 text-gray-600 leading-relaxed" data-id="element-2704" data-mp="3c23yoz6yum6n">{a}</p>}
    </div>;
};
export const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [isMarketplaceModalOpen, setIsMarketplaceModalOpen] = useState(false);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <MarketplaceModal isOpen={isMarketplaceModalOpen} onClose={() => setIsMarketplaceModalOpen(false)} data-id="element-2705" data-mp="35nevnskpoyiu" />
      <PageHeader eyebrow="For Brands & Marketing Agencies" title="Get direct access to hundreds of talent agencies" subtitle="Discover talent, post casting calls and receive curated submissions directly from influencer talent agencies." data-id="element-2706" data-mp="2vneeodabeuht" />

      {/* Stats + beta banner */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-10 text-center" data-id="element-2707" data-mp="yve4sw9dgzzf">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-medium text-gray-500" data-id="element-2708" data-mp="xdfjehekmfz5">
          {stats.map((s, i) => <Fragment key={s} data-id="element-2709" data-mp="sd1br0gvt81n" data-mpi={i}>
              <span data-id="element-2710" data-mp="nfj9tu1a7t35">{s}</span>
              {i < stats.length - 1 && <span className="text-gray-300" aria-hidden="true" data-id="element-2711" data-mp="nfrlxjpkyuda">
                  |
                </span>}
            </Fragment>)}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-zano-lavender/40 border border-zano-pink/20 px-4 py-2 text-sm" data-id="element-2712" data-mp="1g2vjx4q9a8fk">
          <Sparkles size={16} className="text-zano-pink" data-id="element-2713" data-mp="1b7g96lnjosnd" />
          <span className="text-gray-700" data-id="element-2714" data-mp="16d2kf23e1jro">
            Beta Pricing —{' '}
            <span className="font-semibold text-zano-pink" data-id="element-2715" data-mp="11ftycqf4cvl5">
              Lock in 40% off
            </span>{' '}
            for life
          </span>
        </div>
      </div>

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-12" data-id="element-2716" data-mp="1u3pt3pl4odfa">
        <span className={`text-sm font-semibold ${annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2717" data-mp="22clwalr1p53m">
          Annual{' '}
          <span className={annual ? 'text-zano-pink' : 'text-gray-400'} data-id="element-2718" data-mp="26eyzb2eeqowp">
            (save 20%)
          </span>
        </span>
        <button onClick={() => setAnnual(!annual)} aria-label="Toggle annual billing" className="relative w-12 h-6 rounded-full bg-zano-pink/20 transition-colors" data-id="element-2719" data-mp="2bbz83l2n65ri">
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-zano-pink shadow transition-transform ${!annual ? 'translate-x-6' : ''}`} data-id="element-2720" data-mp="2l9dk98hj88e0" />
        </button>
        <span className={`text-sm font-semibold ${!annual ? 'text-zano-charcoal' : 'text-gray-400'}`} data-id="element-2721" data-mp="2u4jssf86t4k8">
          Monthly
        </span>
      </div>

      {/* Tiers */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2722" data-mp="343iwdje3ia0d">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch" data-id="element-2723" data-mp="3923vtgliwpnr">
          {tiers.map((t, i) => <Reveal key={t.name} delay={i * 0.08} className="h-full" data-id="element-2724" data-mp="3uao8o8vvujh5" data-mpi={i}>
              <div className={`relative h-full flex flex-col rounded-2xl border p-7 ${t.highlight ? 'border-zano-pink shadow-float bg-white' : 'border-gray-200 bg-white shadow-sm'}`} data-id="element-2725" data-mp="3pdfqw9enchgf">
                {t.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zano-pink text-white text-xs font-semibold px-3 py-1 rounded-full" data-id="element-2726" data-mp="263wq3exhnj6">
                    Most Popular
                  </span>}
                <h3 className="text-lg font-bold text-gray-900" data-id="element-2727" data-mp="3kdte212qd385">{t.name}</h3>
                <p className={`text-sm text-gray-500 mb-5 ${t.name === 'Casting' ? 'md:whitespace-nowrap' : ''}`} data-id="element-2728" data-mp="3kfwbv7a4k0nf">
                  {t.desc}
                </p>
                <div className="mb-1 flex items-end gap-1" data-id="element-2729" data-mp="2rvfnmmjq4jj3">
                  <span className="text-4xl font-bold text-zano-charcoal" data-id="element-2730" data-mp="2mxe6461y4s9q">
                    {annual ? t.annual : t.monthly}
                  </span>
                  <span className="text-sm text-gray-500 mb-1" data-id="element-2731" data-mp="12ek1wnvi7xps">
                    {annual ? '/ month, billed annually' : '/ month'}
                  </span>
                </div>
                <p className="text-sm font-semibold text-zano-pink mb-6" data-id="element-2732" data-mp="thjjcxv48tu9">
                  {t.note}
                </p>
                {t.name === 'Marketplace' ? <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" onClick={() => setIsMarketplaceModalOpen(true)} data-id="element-2733" data-mp="1m5fevz8y8fpg">
                    {t.cta}
                  </Button> : <a href="https://app.zanoplatform.com/get-started?tier=explorer&type=brand&cycle=monthly" className="block" data-id="element-2734" data-mp="1h3q0qaqfjlx5">
                    <Button variant={t.highlight ? 'primary' : 'secondary'} className="w-full justify-center" data-id="element-2735" data-mp="5r5shao9p119">
                      {t.cta}
                    </Button>
                  </a>}
                <ul className="mt-7 space-y-3" data-id="element-2736" data-mp="aigi76pa2aag">
                  {t.features.map(f => <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700" data-id="element-2737" data-mp="fgrtzmz4uuvb">
                      <Check size={16} className="text-zano-pink mt-0.5 shrink-0" data-id="element-2738" data-mp="1fywctdjbjnz" />{' '}
                      {f}
                    </li>)}
                </ul>
              </div>
            </Reveal>)}
        </div>
      </section>

      <HowMarketplaceWorks audience="brands" background="white" data-id="element-2739" data-mp="1lx3s5o1ygcqw" />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2740" data-mp="1lvamoc32vykh">
        <Reveal data-id="element-2741" data-mp="1qvold3ishw5u">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8" data-id="element-2742" data-mp="1qu3ym02pvhij">
            Frequently asked questions
          </h2>
          <div data-id="element-2743" data-mp="36fxji5natctf">
            {faqs.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-2744" data-mp="36d1lkhbgao4z" />)}
          </div>
        </Reveal>
      </section>

      {/* Bottom CTA */}
      <section className="py-20" data-id="element-2745" data-mp="3gdlnznaphyos">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2746" data-mp="3gck4cufmk3vg">
          <Reveal data-id="element-2747" data-mp="3u47bxqf0oytj">
            <div className="bg-zano-charcoal rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden" data-id="element-2748" data-mp="3u3p7w7m45gb2">
              <div className="absolute top-0 right-0 w-64 h-64 bg-zano-pink/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2749" data-mp="3p544y0fh8iix" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-zano-lavender/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-2750" data-mp="2yfoiysmk0af" />
              <div className="relative z-10 max-w-2xl mx-auto" data-id="element-2751" data-mp="29pwx2daej8fa">
                <h2 className="text-3xl md:text-4xl font-bold mb-5" data-id="element-2752" data-mp="24q2669qmmiu8">
                  Not sure which plan fits?
                </h2>
                <p className="text-lg text-gray-300 mb-8" data-id="element-2753" data-mp="2uezgifujfsx8">
                  Book a demo with us, or ask us anything by emailing{' '}
                  <a href="mailto:support@zanoplatform.com" className="text-white font-semibold underline underline-offset-2 hover:text-zano-pink transition-colors" data-id="element-2754" data-mp="2yhm587vwwxq7">
                    support@zanoplatform.com
                  </a>
                  .
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2755" data-mp="3he5in9dr17sk">
                  <a href="mailto:support@zanoplatform.com?subject=Book%20a%20demo%20with%20Zano" className="w-full sm:w-auto" data-id="element-2756" data-mp="3m5dsg6izqv69">
                    <Button variant="primary" className="bg-white text-zano-charcoal hover:bg-gray-100 w-full sm:w-auto px-8 py-3" data-id="element-2757" data-mp="3w3a18roghe17">
                      <Calendar size={18} className="mr-1.5" data-id="element-2758" data-mp="1pf1b59jrjm6p" />
                      Book a Demo
                    </Button>
                  </a>
                  <a href="mailto:support@zanoplatform.com" className="w-full sm:w-auto" data-id="element-2759" data-mp="1kewqr8fqi7b7">
                    <Button variant="secondary" className="bg-transparent text-white border-gray-600 hover:bg-gray-800 hover:border-gray-500 w-full sm:w-auto px-8 py-3" data-id="element-2760" data-mp="1fiofw78kl09u">
                      <Mail size={18} className="mr-1.5" data-id="element-2761" data-mp="236645ssmlg82" />
                      Email Support
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>;
};